// SIMS-8360 (FR-14) — capture-robustness for external images used on the certificate.
//
// An external image (e.g. the account logo) must be embeddable so it appears in the DOWNLOADED/EMAILED
// PDF, not just on screen: html2canvas can only capture a cross-origin image if it was loaded with CORS
// and re-encoded to a data URI. This helper loads the URL with crossOrigin="anonymous" and returns a
// base64 data URI; if the host blocks CORS, the URL is unreachable, or the host is slow, it resolves to
// "" (empty) so the caller can fall back gracefully — the certificate still generates, never a broken
// image or a hung capture (AC3). Pure/injectable so it is unit-testable without a real network.

export interface ImageLike {
	crossOrigin: string | null;
	onload: (() => void) | null;
	onerror: (() => void) | null;
	src: string;
	naturalWidth: number;
	naturalHeight: number;
}

export interface ResolveDeps {
	/** Factory for an Image (defaults to the DOM Image). Injected in tests. */
	createImage?: () => ImageLike;
	/** Encode a loaded image to a data URI (defaults to a canvas). Injected in tests. */
	toDataURL?: (img: ImageLike) => string;
	/** Max ms to wait for the image before falling back (AC3: a slow host must not hang generation). */
	timeoutMs?: number;
}

const defaultToDataURL = (img: ImageLike): string => {
	const canvas = document.createElement("canvas");
	canvas.width = img.naturalWidth;
	canvas.height = img.naturalHeight;
	const ctx = canvas.getContext("2d");
	// drawImage/toDataURL throw on a tainted (non-CORS) canvas — the caller treats a throw as fallback.
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	ctx?.drawImage(img as any, 0, 0);
	return canvas.toDataURL("image/png");
};

/**
 * Resolve an external image URL to an embeddable base64 data URI, or "" on any failure.
 * Never rejects and never hangs: CORS-block / load-error / timeout all resolve to "".
 */
export function resolveEmbeddableImage(
	url: string,
	deps: ResolveDeps = {},
): Promise<string> {
	const {
		createImage = () => new Image() as unknown as ImageLike,
		toDataURL = defaultToDataURL,
		timeoutMs = 8000,
	} = deps;

	if (!url) return Promise.resolve("");

	return new Promise<string>((resolve) => {
		let settled = false;
		const done = (value: string) => {
			if (settled) return;
			settled = true;
			resolve(value);
		};

		// AC3 (C15944): a slow/never-responding host must not hang certificate generation.
		const timer = setTimeout(() => done(""), timeoutMs);

		const img = createImage();
		img.crossOrigin = "anonymous";
		img.onload = () => {
			clearTimeout(timer);
			try {
				done(toDataURL(img)); // may throw on a tainted canvas → fall back
			} catch {
				done("");
			}
		};
		img.onerror = () => {
			clearTimeout(timer);
			done(""); // unreachable URL / CORS block → fall back (AC3 C15943)
		};
		img.src = url;
	});
}
